<template>
  <ais-instant-search
    :index-name="algoliaIndex"
    :routing="routing"
    :search-client="searchClient"
    class="app-search"
    :future="{
      preserveSharedStateOnUnmount: true,
    }"
  >
    <div class="breadcrumbs">
      <ais-current-refinements v-slot="{ items: supplierItems }" :included-attributes="breadcrumbAttributes">
        <ais-breadcrumb v-slot="{ items, createURL }" :attributes="hierarchicalAttributes">
          <adn-breadcrumbs :breadcrumbs="transformedBreadcrumbs(items, supplierItems, createURL)" />
        </ais-breadcrumb>
      </ais-current-refinements>
    </div>
    <ais-configure v-bind="searchParameters"></ais-configure>
    <ais-search-box v-show="false" ref="searchBox" />
    <div class="search--main">
      <div class="search-filters-container">
        <ais-stats v-if="!isMobile" v-slot="{ query, nbHits }">
          <div class="search-results__title">
            <h1
              v-if="query"
              :class="{ 'search-terms__content--with-selected-category': showSelectedCategory(query, nbHits) }"
              class="search-terms__content"
              data-test="search-terms-content"
            >
              <strong>{{ i18n._('Résultats pour {query}', { query: query }) }}</strong>
            </h1>
            <h1
              v-if="showSelectedCategory(query, nbHits)"
              class="search-results__selected-category"
              :class="{ 'search-results__selected-category--mobile': isMobile }"
            >
              {{ selectedCategory }}
            </h1>
          </div>
        </ais-stats>
        <ais-hierarchical-menu
          ref="categories"
          v-slot="{ items, refine, createURL }"
          class="search-results__menu"
          :attributes="hierarchicalAttributes"
          data-test-cat-menu
          :limit="100"
          :sort-by="compareCategories"
          :transform-items="transformItems"
        >
          <p-hierarchical-mega-menu
            :categories="items"
            :refine="refine"
            :create-u-r-l="searchOrCategory === 'category' ? createURL : undefined"
            @click="onSelectedCategoryChange(items)"
          />
        </ais-hierarchical-menu>
        <ais-stats v-slot="{ query, nbHits }">
          <ais-state-results v-if="isSemences && customFiltersEnabled" v-slot="{ results: { params } }">
            <p-semences-filters :algolia-params="params" />
          </ais-state-results>
          <div class="search-results__header">
            <div v-if="isMobile" class="search-results__title">
              <h1
                v-if="query"
                :class="{ 'search-terms__content--with-selected-category': showSelectedCategory(query, nbHits) }"
                class="search-terms__content"
                data-test="search-terms-content"
              >
                <strong>{{ i18n._('Résultats pour {query}', { query: query }) }}</strong>
              </h1>
            </div>
            <h2 class="search-results__count">
              {{ labelCountProduct(nbHits) }}
            </h2>
            <div class="search-results__filter">
              <adn-button
                kind="secondary"
                icon-name="action-filters"
                icon-position="left"
                class="search-filters__button"
                @click="onClickFilterButton()"
              >
                {{ i18n._('Filtrer') }}
              </adn-button>
            </div>
          </div>
          <p-search-filters
            :breadcrumb-attributes="breadcrumbAttributes"
            :current-cooperative="currentCooperative"
            :hierarchical-attributes="hierarchicalAttributes"
            :is-mobile="isMobile"
            :selected-family-name="selectedFamilyName"
            :nav-bar-is-present="navBarIsPresent"
            :filter-bloc-is-visible="filterBlocIsVisible"
            @set-mobile-filters-block-displayed="onMobileFiltersBlockDisplay"
          />
        </ais-stats>
        <div class="search-filters--tags">
          <ais-current-refinements
            :excluded-attributes="['categoriesLevel0', 'query']"
            :transform-items="labelsForFiltersTags"
          >
            <template #refinement="{ refinement, refine }">
              {{ refinement.label }}
              <button
                type="button"
                class="ais-CurrentRefinements-delete"
                @click="() => onClickRefinement(refinement, refine)"
              >
                ✕
              </button>
            </template>
          </ais-current-refinements>
          <ais-current-refinements :excluded-attributes="['categoriesLevel0', 'query']" v-slot="{ items, refine }">
            <adn-button
              v-if="items.length > 0"
              kind="link"
              class="search-filters__reset-button"
              @click="resetAllFilters(items, refine)"
            >
              {{ i18n._('Réinitialiser') }}
            </adn-button>
          </ais-current-refinements>
        </div>
        <p v-if="categoryDescription && !isMobile" class="search-results__pages-univers" v-html="categoryDescription" />
        <div
          v-if="featuresForCurrentBrand.certificates.phyto && currentCooperativeName && !isCropProtectionAllowed"
          class="story__components-horizontal-container"
        >
          <idf-info-card kind="danger" data-test="crop-protection-alert-error">
            {{
              i18n._(
                'Aladin ne dispose pas de votre certiphyto, merci de contacter votre coopérative afin de pouvoir commander les produits santé du végétal sur la plateforme',
              )
            }}
          </idf-info-card>
        </div>
        <ais-infinite-hits
          :class-names="{ 'ais-InfiniteHits': 'search-hits-previous' }"
          :show-previous="true"
          :show-next="false"
        >
          <template #loadPrevious="{ isFirstPage, refinePrevious }">
            <adn-button
              v-if="!isFirstPage"
              class="search-hits-button"
              kind="primary"
              :disabled="status === 'loading'"
              @click="refinePrevious"
            >
              {{ i18n._('Voir les produits précédents') }}
            </adn-button>
          </template>
        </ais-infinite-hits>
        <p-search-hits :is-authenticated="isAuthenticated" :user="user" @hit="onHit">
          <slot name="search-hits-insertion" />
        </p-search-hits>
        <ais-stats ref="stats" v-slot="{ nbHits, page }">
          <div v-if="nbHits === 0" data-test="no-results-image">
            <div class="search-results__no-results-image">
              <inline-svg src="/themes/current/images/no_search_result.svg" />
            </div>
            <strong>{{ i18n._('Essayez à nouveau avec nos conseils ci-dessous.') }}</strong>
            <div class="search-results__no-results-suggestions">
              <h2>{{ i18n._('Suggestions') }}</h2>
              <ul>
                <li>{{ i18n._(`Vérifiez l'orthographe des mots utilisés`) }}</li>
                <li>{{ i18n._('Utilisez des mots clés plus généraux') }}</li>
                <li>{{ i18n._('Tapez un minimum de mots dans votre recherche') }}</li>
              </ul>
            </div>
          </div>
          <ais-infinite-hits v-slot="{ refineNext, isLastPage }">
            <ais-state-results v-slot="{ status }" class="search-filters-more" :catch-error="true">
              <div v-show="nbHits > 0" class="infinite-hits-container">
                <span class="product-counter">
                  {{ i18n._('Vous avez vu') }}
                  {{ computeDisplayedProductsCount(searchParameters.hitsPerPage, nbHits) }}
                  {{ i18n._('produits sur') }}
                  {{ nbHits }}
                </span>

                <p-progress-bar
                  class="products-progress-bar"
                  :value="computeProgressBarValue(searchParameters.hitsPerPage, nbHits)"
                />
              </div>
              <adn-button
                v-if="!isLastPage"
                class="search-hits-button"
                kind="primary"
                :disabled="status === 'loading'"
                @click="refineNext"
              >
                {{ i18n._('Voir plus de produits') }}
              </adn-button>
              <div v-if="updatePage(page, isLastPage)"></div>
            </ais-state-results>
          </ais-infinite-hits>
        </ais-stats>
        <slot name="search-hits-footer-insertion" />
      </div>
      <div v-if="isMobile">
        <div v-if="categoryDescription" class="search-filters-container">
          <h1 class="search-description--mobile">
            {{ selectedCategory }}
          </h1>
          <div class="search-results__pages-univers" v-html="categoryDescription" />
        </div>
      </div>
    </div>
  </ais-instant-search>
</template>

<script>
import { useMobileListener } from '@b2ag/utils/src/useMobileListener'
import { defineComponent, inject, ref } from 'vue'
import { mapGetters } from 'vuex'
import InlineSvg from 'vue-inline-svg'
import { searchSEOTitle } from '@b2ag/utils/src/SEO/title'
import { searchSEODescription } from '@b2ag/utils/src/SEO/description'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import AdnBreadcrumbs from '@b2ag/design-system/src/components/AdnBreadcrumbs/AdnBreadcrumbs.vue'
import { getSecondsSinceEpoch } from '@b2ag/utils/src/secondsSinceEpoch'
import { currentAppBrand } from '@b2ag/app-brands'
import IdfInfoCard from '@b2ag/design-system/src/components/IdfInfoCard/IdfInfoCard.vue'
import { featuresForCurrentBrand } from '@b2ag/app-brands/dist/features'
import { i18n } from '@b2ag/locale'
import PHierarchicalMegaMenu from '../PHierarchicalMegaMenu/PHierarchicalMegaMenu.vue'
import PSearchHits from '../PSearchHits/PSearchHits.vue'
import PSearchFilters from '../PSearchFilters/PSearchFilters.vue'
import PProgressBar from '../PProgressBar/PProgressBar.vue'
import { CUSTOM_FACETS, FALSY_FACETS } from '../PSearchFilters/facets.setting'
import { OTHER_CATEGORY, SUPPLIER_REFINEMENT } from './constants'
import { createCategoryRouting, createSearchRouting } from './algoliaRouting'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import {
  AisBreadcrumb,
  AisConfigure,
  AisCurrentRefinements,
  AisHierarchicalMenu,
  AisInfiniteHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es'
import { getAppContext } from '@b2ag/micro-front-tools'
import PSemencesFilters from '../PSemencesFilters/PSemencesFilters.vue'
import { useFeatureFlipping } from '@b2ag/feature-flipping'
import { useCustomFilter } from '../../composables/useCustomFilter'
import { useHead } from '#imports'
import { getConfig } from '@b2ag/micro-front-tools'

const HITS_PER_PAGE = 24

export default defineComponent({
  name: 'PAppSearch',
  components: {
    PProgressBar,
    IdfInfoCard,
    PHierarchicalMegaMenu,
    PSearchHits,
    PSearchFilters,
    AdnBreadcrumbs,
    InlineSvg,
    AdnButton,
    AisBreadcrumb,
    AisCurrentRefinements,
    AisHierarchicalMenu,
    AisInfiniteHits,
    AisStats,
    AisInstantSearch,
    AisConfigure,
    AisSearchBox,
    AisStateResults,
    PSemencesFilters,
  },
  props: {
    algoliaMainIndex: {
      type: String,
      default: '',
    },
    categoryDescription: {
      type: String,
      default: '',
    },
    algoliaIndexCoopPrefix: {
      type: String,
      default: '',
    },
    algoliaSearchClient: {
      type: Object,
      required: true,
    },
    currentCooperative: {
      type: Object,
      default: () => ({}),
    },
    searchQuery: {
      type: String,
      default: '',
    },
    selectedCategoryName: {
      type: String,
      default: '',
    },
    selectedFamilyName: {
      type: String,
      default: '',
    },
    selectedSupplier: {
      type: Object,
    },
    seoDescription: {
      type: String,
    },
    seoTitle: {
      type: String,
    },
    isCropProtectionAllowed: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    navBarIsPresent: {
      type: Boolean,
      default: true,
    },
    searchOrCategory: {
      type: String,
      default: 'search',
    },
  },
  setup(props) {
    const { isMobile } = useMobileListener()
    const getFamilyBySlug = inject('getFamilyBySlug')
    const getCategoriesArrayFromSlug = inject('getCategoriesArrayFromSlug')
    const getSlugFromCategories = inject('getSlugFromCategories')
    const categoriesBySlug = inject('categoriesBySlug')
    const { isCustomFiltersEnabled, isDefaultCustomFiltersEnabled } = useFeatureFlipping()
    const {
      additionalFilters,
      selectedFilters,
      removeFilterValue,
      resetCropFilter,
      updateAdditionalFiltersByCrop,
      resetCustomFilters,
    } = useCustomFilter()

    // Transformation des données de data() en refs et reactives
    const routing = ref(undefined)
    const breadcrumbAttributes = ref([
      'query',
      'supplier',
      'categoriesLevel0',
      'categoriesLevel1',
      'categoriesLevel2',
      'categoriesLevel3',
    ])
    const hierarchicalAttributes = ref(['categoriesLevel0', 'categoriesLevel1', 'categoriesLevel2', 'categoriesLevel3'])
    const cooperativeIndexNotExistOrEmpty = ref(false)
    const oldSelectedCategory = ref('')
    const nbHits = ref(undefined)
    const filterBlocIsVisible = ref(false)
    const canonicalUrl = ref('')
    const prevUrl = ref('')
    const nextUrl = ref('')
    const currentPage = ref(0)
    const isLastPage = ref(false)
    const maxPageViewed = ref(0)
    const minPageViewed = ref(Number.MAX_VALUE)

    useHead(() => {
      const canonicalUrlValue = canonicalUrl.value
      const prevUrlValue = prevUrl.value
      const nextUrlValue = nextUrl.value
      const meta = {
        title: searchSEOTitle({
          appContext: currentAppBrand,
          category: props.selectedCategoryName,
          family: props.selectedFamilyName,
          page: currentPage.value,
          title: props.seoTitle,
        }),
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content:
              props.seoDescription ||
              searchSEODescription(props.selectedFamilyName, props.selectedCategoryName, currentAppBrand),
          },
        ],
        link: [{ vmid: 'canonical', rel: 'canonical', href: canonicalUrlValue }],
      }
      if (!!props.selectedFamilyName || !!props.selectedCategoryName) {
        meta.meta.push({ vmid: 'robots', name: 'robots', content: 'index, follow' })
      } else {
        meta.meta.push({ vmid: 'robots', name: 'robots', content: 'noindex, follow' })
      }

      if (prevUrlValue) {
        meta.link.push({ rel: 'prev', href: prevUrlValue })
      }
      if (nextUrlValue) {
        meta.link.push({ rel: 'next', href: nextUrlValue })
      }
      return meta
    })

    return {
      // Retourner les refs et reactives transformés de data()
      routing,
      breadcrumbAttributes,
      hierarchicalAttributes,
      cooperativeIndexNotExistOrEmpty,
      oldSelectedCategory,
      nbHits,
      filterBlocIsVisible,
      canonicalUrl,
      prevUrl,
      nextUrl,
      currentPage,
      isLastPage,
      maxPageViewed,
      minPageViewed,

      // Retourner les valeurs injected et composables
      categoriesBySlug,
      featuresForCurrentBrand,
      getCategoriesArrayFromSlug,
      getFamilyBySlug,
      getSlugFromCategories,
      isCustomFiltersEnabled,
      isDefaultCustomFiltersEnabled,
      isMobile,
      additionalFilters,
      selectedFilters,
      resetCropFilter,
      updateAdditionalFiltersByCrop,
      resetCustomFilters,
      removeFilterValue,
      i18n,
    }
  },
  data() {
    return {
      searchClient: this.algoliaSearchClient,
    }
  },
  computed: {
    algoliaIndex() {
      const locale = i18n.locale
      if (this.currentCooperativeId) {
        if (this.cooperativeIndexNotExistOrEmpty) {
          return `${this.algoliaIndexCoopPrefix + this.currentCooperativeId}.${locale}.old`
        }
        return `${this.algoliaIndexCoopPrefix}${this.currentCooperativeId}.${locale}`
      }
      return `${this.algoliaMainIndex}.${locale}`
    },
    searchParameters() {
      const device = window.innerWidth < 992 ? 'mobile' : 'desktop'
      const searchParameters = {
        hitsPerPage: HITS_PER_PAGE,
        analytics: true,
        maxValuesPerFacet: 500,
        analyticsTags: [`front:${getAppContext().appFrontType}`, `device:${device}`],
      }
      const secondsSinceEpoch = getSecondsSinceEpoch()
      searchParameters.filters = `variants.sellable_until > ${secondsSinceEpoch} AND ${
        !this.currentCooperativeId ? 'variants.is_offline:false' : 'is_published:true'
      }`

      return searchParameters
    },
    selectedCategory() {
      if (this.selectedCategoryName) {
        return this.selectedCategoryName
      }
      if (this.selectedFamilyName.length > 0) {
        return this.selectedFamilyName
      }
      return i18n._('Tous les produits')
    },
    currentCooperativeId() {
      return this.currentCooperative.id
    },
    currentCooperativeName() {
      return this.currentCooperative.name
    },
    pageViewed() {
      return this.maxPageViewed - this.minPageViewed + 1
    },
    isSemences() {
      if (!this.selectedFamilyName) {
        return false
      }
      const slug = this.getSlugFromCategories([this.selectedFamilyName])
      return this.categoriesBySlug.get(slug).objectID.includes('semences')
    },
    customFiltersEnabled() {
      return this.$props.isAuthenticated ? this.isCustomFiltersEnabled : this.isDefaultCustomFiltersEnabled
    },
    ...mapGetters(['getCategories']),
  },
  watch: {
    searchQuery(query) {
      this.$refs.searchBox.currentRefinement = query
    },
    algoliaIndex() {
      this.$emit('algoliaIndexChanged')
    },
  },
  async created() {
    if (this.searchOrCategory === 'category') {
      this.routing = createCategoryRouting(
        this.algoliaIndex,
        this.getCategoriesArrayFromSlug,
        this.getSlugFromCategories,
      )
    } else {
      this.routing = createSearchRouting(this.algoliaIndex, this.getSlugFromCategories)
    }

    if (this.currentCooperativeId) {
      const locale = i18n.locale
      const coopReferentialIndex = this.searchClient.initIndex(
        `${this.algoliaIndexCoopPrefix}${this.currentCooperativeId}.${locale}`,
      )
      try {
        const { nbHits } = await coopReferentialIndex.search('')
        this.cooperativeIndexNotExistOrEmpty = !nbHits
      } catch (_) {
        this.cooperativeIndexNotExistOrEmpty = true
      }
    }
  },
  updated() {
    this.$emit('categoryUpdate', this.selectedCategory)
  },
  methods: {
    onClickRefinement(refinement, refine) {
      refine(refinement)
      this.handleRefinementRemoval(refinement)
    },
    handleRefinementRemoval(refinement) {
      if (refinement.attribute === 'supplier') {
        const query = { ...this.$route.query }
        delete query['menu[supplier]']
        this.$router.replace({ query })
      }

      if (refinement.attribute === 'crop_oad') {
        this.resetCropFilter()
      } else if (this.additionalFilters && this.additionalFilters.includes(refinement.attribute)) {
        this.removeFilterValue(refinement.attribute, refinement.value)
      }
    },
    onPageChange(page) {
      window.scrollTo(0, 0)
      this.$emit('searchPageChange', page)
    },
    showSelectedCategory(query, nbHits) {
      return !this.isViewingAllProducts() || (!query && (nbHits > 0 || this.isViewingAllProducts()))
    },
    compareCategories(firstCategory, secondCategory) {
      if (firstCategory.name === OTHER_CATEGORY) return 1
      if (secondCategory.name === OTHER_CATEGORY) return -1
      return firstCategory.name.localeCompare(secondCategory.name)
    },
    onHit(products) {
      this.$emit('hit', products)
    },
    isViewingAllProducts() {
      return this.selectedCategory === i18n._('Tous les produits')
    },
    labelCountProduct(count = 0) {
      if (count > 1) {
        return `${count} ${i18n._('produits')}`
      } else if (count === 1) {
        return `${count} ${i18n._('produit')}`
      } else {
        return `0 ${i18n._('produit')}`
      }
    },
    onSelectedCategoryChange(items) {
      const path = this.getFullCategoriesPath(items)
      if (this.oldSelectedCategory !== this.selectedCategory) {
        if (this.$route.query.query) {
          const payload = {
            search_term: this.$route.query.query,
            click_category: this.selectedCategory,
          }
          useAnalyticService().trackEvent('page_search_results_click_suggested_category', payload)
        }
        this.oldSelectedCategory = this.selectedCategory
        this.$emit('categoryDescriptionUpdate', { selectedCategory: this.selectedCategory, fullCategoryPath: path })
      }
    },
    getFullCategoriesPath(categories) {
      const path = []
      let currentItem = categories?.find((e) => e.isRefined)
      if (currentItem) path.push(currentItem.label)
      while (currentItem) {
        currentItem = currentItem.data && currentItem.data.find((e) => e.isRefined)
        if (currentItem) path.push(currentItem.label)
      }
      return path
    },
    onMobileFiltersBlockDisplay(displayed) {
      this.filterBlocIsVisible = displayed
      this.$emit('setMobileFiltersBlockDisplayed', displayed)
    },
    computeDisplayedProductsCount(hitsPerPage, nbHits) {
      return Math.min(hitsPerPage * this.pageViewed, nbHits)
    },
    computeProgressBarValue(hitsPerPage, nbHits) {
      if (nbHits === 0) {
        return 0
      }
      // Calculer la valeur avec une limite minimum de 0 et maximum de 100
      const value = Math.min(100, Math.max(0, (hitsPerPage / nbHits) * 100))
      return isFinite(value) ? value : 0
    },
    onClickFilterButton() {
      this.filterBlocIsVisible = true
    },
    labelsForFiltersTags(items) {
      const mapName = { ...CUSTOM_FACETS, ...FALSY_FACETS }
      return items.map((item) => {
        if (Object.keys(mapName).includes(item.attribute)) {
          const updatedItem = {
            ...item,
            refinements: item.refinements.map((refinement) => ({
              ...refinement,
              label: mapName[refinement.attribute],
            })),
          }
          return updatedItem
        }
        return item
      })
    },
    updatePage(page, isLastPage) {
      this.currentPage = page + 1
      this.minPageViewed = Math.min(this.minPageViewed, this.currentPage)
      this.maxPageViewed = Math.max(this.maxPageViewed, this.currentPage)
      this.isLastPage = isLastPage
      this.updateMetaInfo()
      return null
    },
    constructUrlWithPage(pageNumber) {
      const selectedCategories = this.$store.getters.getSelectedCategories
      const frontUrl = getConfig('FRONT_URL') || ''
      const categorySlug = this.getSlugFromCategories(selectedCategories)
      const currentCategory = this.categoriesBySlug.get(categorySlug)
      const baseUrl = `${frontUrl}/${currentCategory?.slug ? `category/${currentCategory.slug}` : 'recherche'}`
      const queryParams = new URLSearchParams(window.location.search)

      if (pageNumber > 1) {
        queryParams.set('page', pageNumber)
      } else {
        queryParams.delete('page')
      }

      let fullUrl = baseUrl + (queryParams.toString() ? `?${queryParams}` : '')
      fullUrl = fullUrl.replace(/\+/g, '%20')
      return fullUrl
    },
    updateMetaInfo() {
      this.canonicalUrl = this.constructUrlWithPage(this.currentPage)
      this.prevUrl = this.currentPage > 1 ? this.constructUrlWithPage(this.currentPage - 1) : null
      this.nextUrl = this.isLastPage ? null : this.constructUrlWithPage(this.currentPage + 1)
    },
    transformedBreadcrumbs(items, supplierItems, createURL) {
      const breadcrumbs = [
        { name: i18n._('Accueil'), route: { name: 'home' } },
        { name: i18n._('Tous les produits'), link: createURL() },
      ]
      let supplierBreadcrumb
      if (supplierItems) {
        const supplierItem = supplierItems.find((refinement) => refinement.attribute === 'supplier')
        if (supplierItem && supplierItem.refinements && supplierItem.refinements.length) {
          supplierBreadcrumb = {
            name: supplierItem.refinements[0].label,
            route: { name: 'search', query: { [SUPPLIER_REFINEMENT]: supplierItem.refinements[0].label } },
          }
        }
      }
      const transformedItems = items.map((item) => {
        if (!item.value) return { name: item.label, route: undefined }
        return {
          name: item.label,
          link: createURL(item.value),
        }
      })

      if (supplierBreadcrumb) breadcrumbs.push(supplierBreadcrumb)

      return breadcrumbs.concat(transformedItems)
    },
    transformItems(items) {
      const categoryOrder = this.getCategories.map((cat) => cat.label)

      return items.sort((a, b) => {
        const indexA = categoryOrder.indexOf(a.label)
        const indexB = categoryOrder.indexOf(b.label)
        return indexA - indexB
      })
    },
    resetAllFilters(items, refine) {
      items.forEach((item) => {
        item.refinements.forEach((refinement) => {
          refine(refinement)
          this.handleRefinementRemoval(refinement)
        })
      })

      this.resetCustomFilters()
    },
  },
})
</script>
<style lang="scss" src="./PAppSearch.scss"></style>
<style lang="scss" src="./PAppSearchAis.scss"></style>
